import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SKIP_ERROR_NOTIFICATION } from '@core/error-handling/services/http-error-notification.interceptor';
import { ContextService } from '@core/context/services/context.service';
import { ContextApiService } from '@core/context/services/context-api.service';
import { environment } from '@environments/environment';
import { ContextoPWA } from '@shared/types';
import {
  PlanejamentoAulaApiItem,
  PlanejamentoAulaApiResponse,
  PlanejamentoCamposAdicionaisRaw,
  PlanejamentoFiltros
} from '../models/planejamento-aula.types';


@Injectable({
  providedIn: 'root'
})
export class PlanejamentoAulaService {
  private readonly apiUrl = environment.apiUrl;
  private readonly quietContext = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true);

  constructor(
    private http: HttpClient,
    private contextService: ContextService,
    private contextApiService: ContextApiService
  ) {}

  /** Itens educacionais (disciplinas) da turma, como array bruto. Map/enriquecimento ficam no componente pai. */
  getItensEducacionaisRaw(
    turmaId: number
  ): Observable<Array<{ id: number; descricao?: string; nome?: string }>> {
    return this.contextApiService.getComponentesCurriculares(turmaId);
  }


  /** Itens brutos da API (sem mapeamento pra view model). */
  getRegistrosRaw(
    contexto: ContextoPWA | null,
    filtros?: PlanejamentoFiltros,
    offset = 0,
    limit = 20
  ): Observable<PlanejamentoAulaApiItem[]> {
    if (!contexto?.turma?.id || !contexto?.servidor?.id) {
      return of([]);
    }

    let filter = `turma.id in (${contexto.turma.id}) and servidor.id = ${contexto.servidor.id}`;

    if (filtros?.termo?.trim()) {
      const termoLimpo = filtros.termo.trim().replace(/"/g, '');
      const termoEncoded = encodeURIComponent(`%${termoLimpo}%`);
      filter += ` and (tema elike "${termoEncoded}")`;
    }
    if (filtros?.data) {
      filter += ` and dataInicio <= ${filtros.data} and dataFim >= ${filtros.data}`;
    }
    if (filtros?.situacao) {
      filter += ` and situacaoAprovacao = "${filtros.situacao}"`;
    }
    if (filtros?.dataInicioPeriodo && filtros?.dataFimPeriodo) {
      filter += ` and dataInicio <= ${filtros.dataFimPeriodo} and dataFim >= ${filtros.dataInicioPeriodo}`;
    }

    let params = new HttpParams()
      .set('filter', filter)
      .set('offset', String(offset))
      .set('limit', String(limit));

    (contexto.componentes ?? []).forEach((c) => {
      params = params.append('itensEducacionais', String(c.id));
    });

    return this.http
      .get<PlanejamentoAulaApiResponse | PlanejamentoAulaApiItem[]>(
        `${this.apiUrl}/conteudos/planejado`,
        { params, context: this.quietContext }
      )
      .pipe(map((res) => this.extrairContent(res)));
  }


  /** Itens de conteúdo (disciplinas, aulas planejadas) de um planejamento; usado no lazy loading dos cards. */
  getItensConteudo(id: number): Observable<any[]> {
    return this.http.get<any>(
      `${this.apiUrl}/conteudos/planejado/${id}/itens-conteudo`,
      { context: this.quietContext }
    ).pipe(
      map((res) => Array.isArray(res) ? res : (res?.content ?? [])),
      catchError(() => of([]))
    );
  }

  getRegistroCompleto(id: number): Observable<PlanejamentoAulaApiItem> {
    return this.http.get<PlanejamentoAulaApiItem>(
      `${this.apiUrl}/conteudos/planejado/${id}`,
      { context: this.quietContext }
    );
  }

  /** Campos adicionais dinâmicos; retorna array ou wrapper — usar normalizarCamposAdicionais() pra normalizar. */
  getCamposAdicionais(id: number): Observable<PlanejamentoCamposAdicionaisRaw> {
    return this.http.get<PlanejamentoCamposAdicionaisRaw>(
      `${this.apiUrl}/conteudos/planejado/campos-adicionais/${id}`,
      { context: this.quietContext }
    );
  }

  /** Períodos avaliativos da turma (replica a lógica da Web para turmas NORMAIS). Array vazio = sem calendário escolar. */
  getPeriodosAvaliativos(idTurma: number): Observable<any[]> {
    return this.http.get<any>(
      `${this.apiUrl}/commons/periodos-avaliativos`,
      {
        params: new HttpParams().set('turma', String(idTurma)),
        context: this.quietContext
      }
    ).pipe(
      map((res) => Array.isArray(res) ? res : (res?.content ?? [])),
      catchError(() => of([]))
    );
  }

  /** Item educacional (replica a lógica da Web para turmas ATIVIDADE/MODULAR). Sem dataInicio/dataFim = sem período definido. */
  getItemEducacional(idComponente: number): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/professores/item-educacional/${idComponente}`,
      { context: this.quietContext }
    ).pipe(
      catchError(() => of(null))
    );
  }

  /** Encerramentos da turma, usados pra identificar períodos encerrados (cadeado). */
  getEncerramentosTurma(idTurma: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrl}/commons/encerramento/encerramentos-turma/${idTurma}`,
      { context: this.quietContext }
    ).pipe(
      map((res) => Array.isArray(res) ? res : (res as any)?.content ?? []),
      catchError(() => of([]))
    );
  }

  getConfiguracoesEstabelecimento(idTurma: number): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/conteudos/planejado/configuracoes-estabelecimentos/${idTurma}`,
      { context: this.quietContext }
    );
  }

  /** Normaliza resposta paginada ({ content: T[] }) ou array direto (T[]). */
  private extrairContent(
    res: PlanejamentoAulaApiResponse | PlanejamentoAulaApiItem[]
  ): PlanejamentoAulaApiItem[] {
    if (Array.isArray(res)) {
      return res;
    }
    return res?.content ?? [];
  }

}
